import React, { FC, Fragment, useEffect, useState } from 'react';
import { formatDate } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { Invoice, InvoiceLineItem, InvoiceType, InvoicingType } from 'models/invoice';
import { Box, Grid, Theme, Typography, useMediaQuery, Alert } from '@mui/material';
import InvoiceAddressBox from './invoice-address-box.component';
import InvoiceLineItemList from './invoice-line-item-list.component';
import InvoiceTotal from './invoice-total.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { Order } from 'models/order';

type InvoiceDetailsProps = {
    invoiceDetails: Invoice;
    handleCertifyUnCertify: (isCertified: boolean) => void;
    isShowOrderCreatorEnabled: boolean;
};
const InvoiceDetails: FC<InvoiceDetailsProps> = ({
    invoiceDetails,
    handleCertifyUnCertify,
    isShowOrderCreatorEnabled,
}: InvoiceDetailsProps) => {
    const { t: translate } = useTranslation(['invoices', 'orders', 'common', 'organization']);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const gridSpace = isMobile ? 1 : 2;
    const currentUser = currentUserVar();
    const isDentist = permissions.isDentist(currentUser?.type);
    const [invoice, setInvoice] = useState<Invoice>({} as Invoice);

    useEffect(() => {
        setInvoice(invoiceDetails);
    }, [invoiceDetails]);

    const onCertifyUnCertify = (order: Order) => {
        const invoiceLineItems = JSON.parse(JSON.stringify(invoice.lineItems));
        const lineItem = invoiceLineItems.find(
            (lineItem: InvoiceLineItem) => (lineItem.order as Order)?._id === order._id,
        );
        if (lineItem) {
            lineItem.order = { ...lineItem.order, ...order };
        }
        setInvoice({ ...invoice, lineItems: invoiceLineItems });
        handleCertifyUnCertify(order.isCertified);
    };

    return (
        <Fragment>
            <Grid container spacing={gridSpace}>
                <Grid item xs={12} md={6}>
                    <InvoiceAddressBox
                        title={translate('INVOICE_ADDRESS')}
                        name={invoice.customer?.contact?.address?.name || invoice.customer?.name}
                        contact={invoice.customer?.contact}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <InvoiceAddressBox
                        title={translate('DELIVERY_ADDRESS')}
                        name={invoice.deliveryAddress?.name}
                        contact={invoice.deliveryAddress?.contact}
                    />
                </Grid>

                <Grid item sm={12} md={3}>
                    <Box mb={2}>
                        <Typography variant="subtitle2">{translate('INVOICE_NUMBER')}</Typography>
                        {invoice.invoiceNumber}
                    </Box>
                    <Box>
                        <Typography variant="subtitle2">{translate('CUSTOMER_ORG_NUMBER')}</Typography>
                        {invoice.customer?.number}
                    </Box>
                    {invoice.vatNumber ? (
                        <Box mt={2}>
                            <Typography variant="subtitle2">{translate('organization:VAT_NUMBER')}</Typography>
                            {invoice.vatNumber}
                        </Box>
                    ) : undefined}
                    {invoice.invoiceType === InvoicingType.SINGLE &&
                    isShowOrderCreatorEnabled &&
                    (invoice.order as Order)?.created?.by ? (
                        <Box mt={2}>
                            <Typography variant="subtitle2">{translate('orders:ORDER_CREATED_BY')}</Typography>
                            {(invoice.order as Order).created.by.fullName}
                            {(invoice.order as Order).isCreatedByLab && ` (${translate('orders:BY_LAB_PERSONNEL')})`}
                        </Box>
                    ) : undefined}
                </Grid>
                <Grid item sm={12} md={3}>
                    <Box mb={2}>
                        <Typography variant="subtitle2">{translate('INVOICE_DATE')}</Typography>
                        {formatDate(invoice.invoiceDate, 'date')}
                    </Box>
                    {invoice.customer?.reference ? (
                        <Box mb={2}>
                            <Typography variant="subtitle2">{translate('YOUR_REFERENCE')}</Typography>
                            {invoice.customer.reference}
                        </Box>
                    ) : undefined}
                    {invoice.ocrNumber ? (
                        <Box>
                            <Typography variant="subtitle2">{translate('OCR_NUMBER')}</Typography>
                            {invoice.ocrNumber}
                        </Box>
                    ) : undefined}
                </Grid>
                <Grid item sm={12} md={3}>
                    <Box mb={2}>
                        <Typography variant="subtitle2">{translate('DUE_DATE')}</Typography>
                        {formatDate(invoice.dueDate, 'date')}
                    </Box>
                    {invoice.orderNumber ? (
                        <Box mb={2}>
                            <Typography variant="subtitle2">{translate('ORDER_NUMBER')}</Typography>
                            {invoice.orderNumber}
                        </Box>
                    ) : undefined}
                    {invoice.ourReference ? (
                        <Box>
                            <Typography variant="subtitle2">{translate('OUR_REFERENCE')}</Typography>
                            {invoice.ourReference}
                        </Box>
                    ) : undefined}
                </Grid>
                <Grid item sm={12} md={3}>
                    <Box mb={2}>
                        <Typography variant="subtitle2">{translate('PAYMENT_TERM')}</Typography>
                        {invoice.paymentTerm} {translate('DAYS_NET')}
                    </Box>
                    {invoice.dentist?.fullName ? (
                        <Box>
                            <Typography variant="subtitle2">{translate('common:DENTIST')}</Typography>
                            {invoice.dentist.fullName}
                        </Box>
                    ) : undefined}
                </Grid>
            </Grid>

            <Box mt={4}>{invoice.description?.split('\n').map((row, idx) => <div key={idx}>{row}</div>)}</Box>

            <Box mt={4}>
                {invoice.lineItems ? (
                    <InvoiceLineItemList
                        invoiceType={invoice.invoiceType as InvoiceType}
                        handleCertifyUnCertify={onCertifyUnCertify}
                        isDentist={isDentist}
                        lineItems={invoice.lineItems}
                        currency={invoice.total.currency}
                    />
                ) : (
                    <Alert severity="info">{translate('DOWNLOAD_PDF_FOR_MORE_DETAILS')}</Alert>
                )}
            </Box>

            <Box mt={4}>
                <InvoiceTotal invoice={invoice} />
            </Box>

            {invoice.invoiceText ? (
                <Box mt={2}>
                    <Typography variant="body2" dangerouslySetInnerHTML={{ __html: invoice.invoiceText }} />
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default InvoiceDetails;
